import { apiFetch } from "@/lib/apiClient";
import { mockAwareFetch } from "@/portals/clinician/api/transport";
import type { EmpiRecordGolden } from "@/shared/types/empi";
import type { EmpiIdentityReason } from "@/shared/types/empi";

export type UploadJobStatus = "processing" | "complete" | "failed" | "cancelled";

/** A record that could not be saved — either a recognized resource type missing a
 *  required field, or one whose data crashed during mapping/validation. Neither the
 *  system nor the patient can fix these, so they're surfaced as a transparency log
 *  (layered into ZipContentsModal) rather than something to action. */
/** Which section of the upload's Issues an issue belongs to (the backend's upload_issues.py). */
export type IssueGroup = "unreadable" | "not_imported" | "note";

export type ParsingIssue = {
  resource_type: string;
  source_file: string | null;
  error_type: string;
  reason: string;
  /** Set by the backend for every issue it serves (absent on the upload-time zip scan). */
  group?: IssueGroup | null;
  title?: string | null;
  /** "upload_again": the patient can fix it by uploading the file again. */
  action?: "upload_again" | null;
  /** Deterministic, content-derived identifier for the specific record this issue is
   *  about — lets the frontend tell apart multiple issues that share the same
   *  resource_type + source_file (e.g. three separate Observations from one C-CDA
   *  file) instead of rendering indistinguishable duplicate cards. Only guaranteed to
   *  match an actually-saved row when error_type is "needs_review"; for other error
   *  types the record itself was never stored, so this identifies content only. Can
   *  be absent on older upload history recorded before this field existed. */
  record_id?: string | null;
};

/** See the backend's app/modules/empi/upload_status.py. */
export type UploadIdentityCheck = {
  state: "checking" | "matched" | "confirmed_by_you" | "needs_your_confirmation" | "not_you";
  /** IdentityReason codes, for needs_your_confirmation. */
  reasons: EmpiIdentityReason[];
  /** The Human Review Queue item to open, for needs_your_confirmation. */
  reviewItemId: string | null;
};

export type UploadResponse = {
  upload_id: string;
  status: UploadJobStatus;
  file_name: string;
  uploaded_at: string;
  /** Populated only for a zip upload — the backend already scanned its contents for
   *  unsupported files synchronously before this response came back (see
   *  upload_service.py's _find_unsupported_zip_entries), so this is known immediately,
   *  well before the worker finishes. null for every other upload path. */
  parsing_issues: ParsingIssue[] | null;
};

export type ZipContentFile = {
  name: string;
  size: number;
  compressed_size: number;
};

/** Thrown by getUploadStatus/listUploads with the real HTTP status attached, so callers
 *  can tell a permanent 404 (id doesn't exist / isn't yours) apart from a transient failure. */
export class UploadStatusError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** Thrown by uploadMedicalZip with whether this was a real rejection (the
 *  server answered — validation failure, unsupported vendor, etc. — so the
 *  upload definitely never happened) or a network-level failure (timeout,
 *  dropped connection, aborted request — the server may have received and
 *  accepted the upload anyway; this client just never found out). Callers
 *  need that distinction to decide whether to show the failure immediately
 *  or try to recover the real upload id first — see
 *  UploadHistoryContext.recoverUploadId. */
export class UploadRequestError extends Error {
  networkError: boolean;
  constructor(message: string, networkError: boolean) {
    super(message);
    this.networkError = networkError;
  }
}

export type UploadStatusResponse = {
  upload_id: string;
  status: UploadJobStatus;
  file_name: string;
  // Patient-entered label from the Upload Records modal's "Title" field —
  // null wherever not given; callers fall back to file_name, same
  // convention as imaging_studies.title.
  title: string | null;
  uploaded_at: string;
  file_count: number | null;
  error_message: string | null;
  files: ZipContentFile[] | null;
  parsing_issues: ParsingIssue[] | null;
  /** Module 5's identity result for the upload — the one identity check, same
   *  rules for every source. null until the upload has finished. */
  identity_check?: UploadIdentityCheck | null;
  // TEMP-TIMING: seconds from upload accepted to the worker fully finishing
  // (see the backend's UploadJob.processed_at) — null until then. Diagnostic
  // only; grep TEMP-TIMING across both repos to remove this feature later.
  processing_seconds: number | null;
  // Both null unless status is "processing" — see the backend's
  // get_upload_processing_progress for what each stage means and how
  // percent is computed (a real fraction of actual page counts where one is
  // known, never a synthetic/timed guess).
  processing_stage: "queued" | "extracting" | "converting" | "ocr" | "ocr_converting" | "processing" | null;
  /** Stage "ocr": pages read by OCR so far, out of the pages that need it. */
  processing_ocr_done?: number | null;
  processing_ocr_total?: number | null;
  processing_progress: number | null;
};

/**
 * Every function below accepts an optional `actingAsProfileId` — pass a
 * Dedicated Profile's id to upload/view/list records *for that profile*
 * instead of the caller's own. Omit it (or pass undefined) for "myself";
 * that's also exactly what happens if you pass your own id. The backend
 * re-checks authorization on every single call (see
 * app/dependencies.py's get_effective_patient_id) — this header carries no
 * trust on its own, it's just which profile you're asking to act on.
 */
function actingAsHeaders(actingAsProfileId?: string): Record<string, string> {
  return actingAsProfileId ? { "X-Acting-As-Profile-Id": actingAsProfileId } : {};
}

/** Uploads an Epic/Athena export .zip for async processing. Do not set Content-Type — the browser sets the multipart boundary.
 *  Pass `signal` to let the caller abort the request client-side (e.g. the patient hits Cancel before the request finishes).
 *  `clientRequestId` — a fresh crypto.randomUUID() generated by the caller before this call, stored on the server's
 *  UploadJob row — lets the caller recover the real upload_id later (getUploadStatusByClientId) if THIS response is
 *  lost to a dropped connection/tunnel reset/timeout, without guessing from the file name and a timestamp. Optional:
 *  omitting it just means recovery isn't possible for that particular upload. */
export async function uploadMedicalZip(file: File, actingAsProfileId?: string, signal?: AbortSignal, clientRequestId?: string, title?: string): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  if (clientRequestId) formData.append("client_request_id", clientRequestId);
  if (title && title.trim()) formData.append("title", title.trim());

  const { data, error, networkError } = await apiFetch<UploadResponse>("/api/v1/data-pipeline/upload", {
    method: "POST",
    body: formData,
    portal: "patient",
    headers: actingAsHeaders(actingAsProfileId),
    signal,
    // The default 30s is wrong for an upload: this response only comes back
    // AFTER the backend has streamed the whole file in, scored/unpacked the
    // zip, pushed it to S3, and committed the row — a multi-MB file over a
    // slow tunnel routinely takes longer than that, and hitting the timeout
    // makes apiFetch report networkError, which sends UploadRecords into
    // recover-by-client-id mode against a row the backend hasn't written
    // yet (the 404 loop). Ignored when the caller passes its own `signal`.
    timeoutMs: 5 * 60_000,
  });

  if (error) {
    throw new UploadRequestError(error.message || "Upload failed", networkError);
  }

  return data as UploadResponse;
}

/** Looks up an upload by the clientRequestId passed to uploadMedicalZip, instead of its upload_id — for when that
 *  call's own response was lost and the real upload_id was never learned. Throws UploadStatusError(404, ...) via
 *  the same path as getUploadStatus if no upload with this token exists (never created, or belongs to someone
 *  else) — indistinguishable from the caller's point of view, exactly like an unknown upload_id would be. */
export async function getUploadStatusByClientId(clientRequestId: string, actingAsProfileId?: string): Promise<UploadStatusResponse> {
  const { data, error, status } = await apiFetch<UploadStatusResponse>(
    `/api/v1/data-pipeline/upload/by-client-id/${clientRequestId}`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) }
  );

  if (error) {
    throw new UploadStatusError(status, error.message || "Failed to fetch upload status");
  }

  return data as UploadStatusResponse;
}

/** One upload's status on its own (the fallback when the batch route isn't there). */
async function fetchOneUploadStatus(uploadId: string, actingAsProfileId?: string): Promise<UploadStatusResponse> {
  const { data, error, status } = await apiFetch<UploadStatusResponse>(
    `/api/v1/data-pipeline/upload/${uploadId}/status`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) }
  );

  if (error) {
    throw new UploadStatusError(status, error.message || "Failed to fetch upload status");
  }

  return data as UploadStatusResponse;
}

// Every upload the app is waiting on is checked in ONE request, at most once
// a second per profile, however many are processing (one request per upload
// every few seconds used to add up and fill the database's connections).
// Calls made close together share a request; each still gets its own answer.
const STATUS_BATCH_GATHER_MS = 50;
const STATUS_BATCH_MIN_GAP_MS = 1000;
const MAX_STATUS_IDS = 50;

type StatusWaiter = { resolve: (status: UploadStatusResponse) => void; reject: (error: unknown) => void };
type PendingStatusBatch = { waiters: Map<string, StatusWaiter[]>; actingAsProfileId?: string };

const pendingStatusBatches = new Map<string, PendingStatusBatch>();
const lastStatusFlushAt = new Map<string, number>();

async function settleStatusChunk(uploadIds: string[], batch: PendingStatusBatch): Promise<void> {
  const settle = (uploadId: string, outcome: (waiter: StatusWaiter) => void) =>
    batch.waiters.get(uploadId)?.forEach(outcome);

  const query = new URLSearchParams();
  uploadIds.forEach((id) => query.append("ids", id));
  const { data, error, status } = await apiFetch<{ data: UploadStatusResponse[] }>(
    `/api/v1/data-pipeline/upload/statuses?${query}`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(batch.actingAsProfileId) }
  );

  if (error && status === 404) {
    // A backend without the batch route yet: one request each, as before.
    uploadIds.forEach((id) =>
      fetchOneUploadStatus(id, batch.actingAsProfileId).then(
        (result) => settle(id, (w) => w.resolve(result)),
        (err) => settle(id, (w) => w.reject(err))
      )
    );
    return;
  }

  if (error) {
    const failure = new UploadStatusError(status, error.message || "Failed to fetch upload status");
    uploadIds.forEach((id) => settle(id, (w) => w.reject(failure)));
    return;
  }

  const byId = new Map((data?.data ?? []).map((item) => [item.upload_id, item]));
  uploadIds.forEach((id) => {
    const found = byId.get(id);
    // Left out = doesn't exist or isn't this patient's, same as the single route's 404.
    if (found) settle(id, (w) => w.resolve(found));
    else settle(id, (w) => w.reject(new UploadStatusError(404, "Upload not found.")));
  });
}

function flushStatusBatch(key: string): void {
  const batch = pendingStatusBatches.get(key);
  if (!batch) return;
  pendingStatusBatches.delete(key);
  lastStatusFlushAt.set(key, Date.now());

  const ids = [...batch.waiters.keys()];
  for (let start = 0; start < ids.length; start += MAX_STATUS_IDS) {
    const chunk = ids.slice(start, start + MAX_STATUS_IDS);
    settleStatusChunk(chunk, batch).catch((err) =>
      chunk.forEach((id) => batch.waiters.get(id)?.forEach((w) => w.reject(err)))
    );
  }
}

/** Polls the live status of a previously-accepted upload while it's parsed asynchronously by the worker.
 *  Batched with every other upload being checked at the same time (see above). */
export function getUploadStatus(uploadId: string, actingAsProfileId?: string): Promise<UploadStatusResponse> {
  const key = actingAsProfileId ?? "";
  return new Promise((resolve, reject) => {
    let batch = pendingStatusBatches.get(key);
    if (!batch) {
      batch = { waiters: new Map(), actingAsProfileId };
      pendingStatusBatches.set(key, batch);
      const sinceLastFlush = Date.now() - (lastStatusFlushAt.get(key) ?? 0);
      setTimeout(() => flushStatusBatch(key), Math.max(STATUS_BATCH_GATHER_MS, STATUS_BATCH_MIN_GAP_MS - sinceLastFlush));
    }
    const waiters = batch.waiters.get(uploadId) ?? [];
    waiters.push({ resolve, reject });
    batch.waiters.set(uploadId, waiters);
  });
}

/** Default/max page size for listUploads — mirrors the backend's own
 *  _UPLOADS_DEFAULT_LIMIT/_UPLOADS_MAX_LIMIT (upload_router.py). "Your
 *  Uploads" is a recent-activity list, not a records table, so 20 covers
 *  several screens of scroll per fetch without over-fetching. */
export const UPLOADS_PAGE_SIZE = 20;

export type UploadListResponse = {
  success: boolean;
  data: UploadStatusResponse[];
  count: number;
  hasMore: boolean;
};

/**
 * One page of upload history for the authenticated patient (or the acting-as
 * profile), newest first. Paginated (limit/offset) — the backend returns a
 * patient's entire upload history unbounded otherwise, which doesn't scale
 * as history grows. Pass `offset` to fetch the next page; `hasMore` in the
 * response tells the caller whether another page exists past this one.
 */
export async function listUploads(
  actingAsProfileId?: string,
  options?: { limit?: number; offset?: number },
): Promise<UploadListResponse> {
  const params = new URLSearchParams();
  params.set("limit", String(options?.limit ?? UPLOADS_PAGE_SIZE));
  params.set("offset", String(options?.offset ?? 0));

  const { data, error } = await apiFetch<UploadListResponse>(
    `/api/v1/data-pipeline/uploads?${params.toString()}`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) }
  );

  if (error) {
    throw new Error(error.message || "Failed to fetch upload history");
  }

  // A 2xx response whose body failed to parse (apiFetch's `data: null` with
  // no `error`) is a real failure, not "this patient genuinely has zero
  // uploads" — silently defaulting to empty here would blank out an
  // already-populated history on a transient hiccup instead of surfacing it.
  if (!data) {
    throw new Error("Received an empty or invalid response. Please try again.");
  }

  return data;
}

/** One parsed clinical record — shape is shared across every resource type; only
 *  `fields` varies (medication_name/dose_quantity for a MedicationRequest,
 *  encounter_class/start_date for an Encounter, etc). */
export type ParsedRecord = {
  id: string;
  resourceType: string;
  sourceVendor: string;
  uploadId: string | null;
  confidenceScore: number;
  needsReview: boolean;
  reviewReason: string | null;
  createdAt: string;
  updatedAt: string;
  fields: Record<string, unknown>;
  /** Module 2: MedicationRequest only. Non-null while the record is part of an
   *  open review item; records sharing a reviewItemId are one conflict. Absent
   *  on other resource types and on backends that predate Module 2. */
  review?: RecordReview | null;
  /** Module 2: how the record's current value was settled. null while under review. */
  origin?: RecordOrigin | null;
  /** Module 5: what a clinician's decision did to this record (origin stays CLINICIAN_VERIFIED). */
  reviewOutcome?: RecordReviewOutcome | null;
  /** Module 5: when reviewOutcome.effect is CORRECTED, which field keys the clinician corrected
   *  (their corrected values are already applied in `fields`). */
  correctedFields?: string[];
  /** Combined view (scope=all) only: the same record was found in several
   *  uploads/syncs and is shown once — `sources` lists every copy, this one first. */
  duplicates?: RecordDuplicates | null;
  /** MedicationRequest: confidently matched to a standard RxNorm medicine. */
  rxnormVerified?: boolean;
  /** MedicationRequest: the RxNorm code (RxCUI) it was matched to; null when none was found. */
  rxcui?: string | null;
  /** MedicationRequest: the title for a confidently identified medicine — its generic
   *  product, readable, plus the brand when every copy is that brand. Null: show the
   *  source's own text (not identified, no single generic, or a clinician-corrected name). */
  displayName?: string | null;
  /** MedicationRequest: resolved history, stored before medication review
   *  existed — never raises an issue; shown under "Earlier records". */
  reviewBaseline?: boolean;
  /** Module 5, combined view (scope=all), Condition / MedicationRequest /
   *  AllergyIntolerance / Observation: this row is a resolved EMPI cluster's
   *  golden record and stands for every source in `golden.sources` (such a
   *  row never carries `duplicates`). Absent on every other row. */
  golden?: EmpiRecordGolden | null;
  /** MedicationRequest, combined view, administered doses only: the uploaded
   *  file's name (or e.g. "Epic sync") the dose came from. */
  sourceLabel?: string | null;
  /** DiagnosticReport: the (shown) rows of the results the report itself lists
   *  (FHIR DiagnosticReport.result). Absent when it lists none (grouped by panel instead). */
  resultIds?: string[];
  /** Condition: the patient's own active / inactive / resolved mark, beside the
   *  hospital's status (never changed). Absent when they haven't marked it. */
  patientStatus?: PatientConditionStatus | null;
};

/** The three statuses a diagnosis / health concern can be marked with. */
export type ConditionStatus = "active" | "inactive" | "resolved";

export type PatientConditionStatus = {
  status: ConditionStatus;
  /** ISO date-time the patient marked it. */
  setAt: string;
  /** The hospital's status, in the same three words. */
  hospitalStatus: ConditionStatus;
  /** The hospital recorded it again after the mark and says something else:
   *  the hospital's status is the one shown ("newer information wins"). */
  hospitalNewer: boolean;
};

/** A dose the care team gave during a hospital stay or visit (fields.medication_course):
 *  listed under Administered Medications, labelled "Administered" everywhere else. */
export function isAdministeredDose(record: { resourceType: string; fields: Record<string, unknown> }): boolean {
  return record.resourceType === "MedicationRequest" && record.fields.medication_course === "administered";
}

export type RecordSource = {
  recordId: string;
  uploadId: string | null;
  sourceVendor: string;
  /** The uploaded file's name, or e.g. "Epic sync". */
  sourceLabel: string | null;
  createdAt: string;
  /** MedicationRequest: the medicine as this copy's source wrote it ("Recorded as"). */
  recordedAs?: string | null;
};

export type RecordDuplicates = {
  count: number;
  sources: RecordSource[];
};

export type RecordEffect = "KEPT" | "DISMISSED" | "DISCONTINUED" | "CORRECTED";

export type RecordReviewOutcome = {
  effect: RecordEffect;
  decidedAt: string;
  reviewItemId: string;
};

export type ReviewTriggerType =
  | "VENDOR_DISAGREEMENT"
  | "MISSING_INFORMATION"
  | "LOW_CONFIDENCE_EXTRACTION"
  | "UNRECOGNIZED_NAME"
  | "RXNORM_REVIEW_BAND"
  /** Module 5 (EMPI): is an uploaded record this person? Patient-only. */
  | "IDENTITY_MISMATCH"
  /** Module 5 (EMPI): the patient's records of one fact disagree; a clinician picks. */
  | "GOLDEN_RECORD_CONFLICT";

/** Where an open review item stands (Module 3). */
export type OpenReviewItemStatus = "AWAITING_PATIENT" | "AWAITING_CLINICIAN" | "IN_CLINICIAN_REVIEW" | "NEEDS_MORE_INFO";

export type RecordReview = {
  status: "UNDER_REVIEW";
  reviewItemId: string;
  /** MEMBER / CLUSTER: a GOLDEN_RECORD_CONFLICT item's links (Module 5). */
  recordRole: "EXISTING" | "INCOMING" | "SUBJECT" | "MEMBER" | "CLUSTER";
  triggerType: ReviewTriggerType;
  /** Module 3. Optional only so records from a backend that predates it still type-check. */
  itemStatus?: OpenReviewItemStatus;
  /** The patient may answer now — waiting on them, or already with a
   *  clinician but never answered (a late answer). */
  canRespond?: boolean;
};

export type RecordOrigin = "SYSTEM_RESOLVED" | "PATIENT_REPORTED" | "CLINICIAN_VERIFIED";

export type ParsedRecordsResponse = {
  success: boolean;
  data: ParsedRecord[];
  count: number;
  hasMore?: boolean;
  sourceVendors: string[];
  sourceGroups: SourceGroup[];
  mixedSourceGroups: boolean;
  /** One upload only: rows of each record type in the WHOLE upload (the page is just the newest ones). */
  typeCounts?: Record<string, number> | null;
};

export type SourceGroup = "structured" | "pdf";

export type ParsedRecordsDetail = {
  records: ParsedRecord[];
  count: number;
  hasMore: boolean;
  sourceVendors: string[];
  sourceGroups: SourceGroup[];
  mixedSourceGroups: boolean;
  /** Rows of each record type in the whole upload; null/absent when the server didn't send them. */
  typeCounts?: Record<string, number> | null;
};

/** Parsed records for the authenticated patient (or the acting-as profile) — GET
 *  /api/patient/records. Pass uploadId to scope to a single upload (what "View Data"
 *  uses); pass resourceTypes to narrow which FHIR resource types come back. Omit
 *  both for everything ever parsed. */
const COMBINED_RECORDS_TIMEOUT_MS = 120_000;

export async function getPatientRecords(options?: {
  uploadId?: string;
  resourceTypes?: string[];
  actingAsProfileId?: string;
}): Promise<ParsedRecord[]> {
  const params = new URLSearchParams();
  if (options?.uploadId) {
    params.set("upload_id", options.uploadId);
  } else {
    // The patient's clinical pages show ALL their completed uploads and syncs
    // together, with the same record from several of them merged into one row
    // ("Found in N records") — not just the latest upload. The whole history
    // comes back in one response (duplicates are merged across all of it).
    params.set("scope", "all");
  }
  if (options?.resourceTypes?.length) params.set("resource_types", options.resourceTypes.join(","));
  const query = params.toString();

  // mockAwareFetch is plain apiFetch unless VITE_USE_MOCKS=true (dev/test only).
  const { data, error } = await mockAwareFetch<ParsedRecordsResponse>(
    `/api/patient/records${query ? `?${query}` : ""}`,
    {
      method: "GET", portal: "patient", headers: actingAsHeaders(options?.actingAsProfileId),
      // A whole history can be large; the default 30s would fail it outright.
      ...(options?.uploadId ? {} : { timeoutMs: COMBINED_RECORDS_TIMEOUT_MS }),
    }
  );

  if (error) {
    throw new Error(error.message || "Failed to fetch parsed records");
  }

  return data?.data ?? [];
}

/** Detailed upload-scoped response used only by UploadDetailPanel. Unlike the
 * shared record fetch above, this preserves full-upload source metadata and
 * supports server-side source-group filtering. */
export async function getPatientRecordsDetailed(options: {
  uploadId: string;
  sourceGroup?: SourceGroup;
  actingAsProfileId?: string;
  /** Pass true for the live-poll's own recurring re-fetch while an upload is
   *  still processing — not a patient actually opening/switching to this
   *  view. Skips the backend's HIPAA-access VIEW audit log for this call;
   *  the real "someone looked at this" event is already logged by whichever
   *  fetch is NOT a background poll (first load, tab switch, manual retry). */
  background?: boolean;
}): Promise<ParsedRecordsDetail> {
  // Each type's newest few, not the newest 500 overall: a large file's many
  // lab results can't push its one allergy out of the preview.
  const params = new URLSearchParams({ upload_id: options.uploadId, per_type: String(UPLOAD_PREVIEW_PER_TYPE) });
  if (options.sourceGroup) params.set("source_group", options.sourceGroup);
  if (options.background) params.set("background", "true");
  const { data, error } = await apiFetch<ParsedRecordsResponse>(
    `/api/patient/records?${params.toString()}`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(options.actingAsProfileId) },
  );
  if (error) throw new Error(error.message || "Failed to fetch parsed records");
  return {
    records: data?.data ?? [],
    count: data?.count ?? 0,
    hasMore: data?.hasMore ?? false,
    sourceVendors: data?.sourceVendors ?? [],
    sourceGroups: data?.sourceGroups ?? [],
    mixedSourceGroups: data?.mixedSourceGroups ?? false,
    typeCounts: data?.typeCounts ?? null,
  };
}

/** How many of each record type an upload's preview lists; "Show more" loads the rest. */
export const UPLOAD_PREVIEW_PER_TYPE = 50;
export const UPLOAD_MORE_PAGE_SIZE = 200;

/** The next page of one record type in one upload, newest first (after the preview's). */
export async function getUploadRecordsOfType(options: {
  uploadId: string;
  resourceType: string;
  offset: number;
  sourceGroup?: SourceGroup;
  actingAsProfileId?: string;
}): Promise<{ records: ParsedRecord[]; hasMore: boolean }> {
  const params = new URLSearchParams({
    upload_id: options.uploadId,
    resource_types: options.resourceType,
    limit: String(UPLOAD_MORE_PAGE_SIZE),
    offset: String(options.offset),
  });
  if (options.sourceGroup) params.set("source_group", options.sourceGroup);
  const { data, error } = await apiFetch<ParsedRecordsResponse>(
    `/api/patient/records?${params.toString()}`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(options.actingAsProfileId) },
  );
  if (error) throw new Error(error.message || "Failed to fetch parsed records");
  return { records: data?.data ?? [], hasMore: data?.hasMore ?? false };
}

/** Parsed MedicationRequest records only — the Medications sidebar page and the
 *  Dashboard's Current Medications card both read from this. */
export async function getMedications(actingAsProfileId?: string): Promise<ParsedRecord[]> {
  return getPatientRecords({ resourceTypes: ["MedicationRequest"], actingAsProfileId });
}

/** Parsed DiagnosticReport records only — the Dashboard's Recent Labs card reads
 *  from this. (The full Diagnostics & Imaging sidebar page is still mock-data-driven —
 *  its LabResult/ImagingStudy types need per-component lab values and image metadata
 *  this resource type alone can't provide; not wired up yet.) */
export async function getDiagnosticReports(actingAsProfileId?: string): Promise<ParsedRecord[]> {
  return getPatientRecords({ resourceTypes: ["DiagnosticReport"], actingAsProfileId });
}

/** Parsed Encounter records only — the Dashboard's Recent Visits card and its
 *  modal both read from this. (The full Care & Treatment sidebar page is still
 *  mock-data-driven — same "widget first, full page later" scope as Diagnostics.) */
export async function getEncounters(actingAsProfileId?: string): Promise<ParsedRecord[]> {
  return getPatientRecords({ resourceTypes: ["Encounter"], actingAsProfileId });
}

/** sourceVendor of a record the patient entered themselves (a health concern):
 *  never part of a golden record, never merged with a provider's copy. */
export const PATIENT_SOURCE_VENDOR = "patient";

/** What a patient can enter for their own health concern: only the fields a
 *  provider's concern (a Condition) has too, so the two never differ in shape. */
export type HealthConcernInput = {
  name: string;
  status: ConditionStatus;
  /** "YYYY-MM-DD"; "" clears it on an edit. */
  onsetDate: string;
  note: string;
};

/** Saved as a Condition row (sourceVendor "patient", unconfirmed). */
export async function createHealthConcern(input: HealthConcernInput, actingAsProfileId?: string): Promise<ParsedRecord> {
  const { data, error } = await apiFetch<ParsedRecord>("/api/patient/health-concerns", {
    method: "POST",
    body: JSON.stringify({ ...input, onsetDate: input.onsetDate || null, note: input.note || null }),
    portal: "patient",
    headers: actingAsHeaders(actingAsProfileId),
  });
  if (error || !data) throw new Error(error?.message || "Couldn't save your concern");
  return data;
}

/** Marks a diagnosis / health concern from the patient's records as active,
 *  inactive or resolved. The hospital's record isn't changed; the patient
 *  themselves only (not while viewing someone else's profile). */
export async function setConditionStatus(id: string, status: ConditionStatus): Promise<PatientConditionStatus> {
  const { data, error } = await apiFetch<PatientConditionStatus>(`/api/patient/conditions/${encodeURIComponent(id)}/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
    portal: "patient",
  });
  if (error || !data) throw new Error(error?.message || "Couldn't update the status");
  return data;
}

/** Only the patient's own concerns can be changed; there is no delete (mark resolved instead). */
export async function updateHealthConcern(
  id: string, input: Partial<HealthConcernInput>, actingAsProfileId?: string,
): Promise<ParsedRecord> {
  const { data, error } = await apiFetch<ParsedRecord>(`/api/patient/health-concerns/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
    portal: "patient",
    headers: actingAsHeaders(actingAsProfileId),
  });
  if (error || !data) throw new Error(error?.message || "Couldn't update your concern");
  return data;
}
